import React, { useEffect, useState } from 'react';
import { IoSearchOutline } from 'react-icons/io5';
import { HiOutlineArrowLeft } from 'react-icons/hi';
import { FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaAngleDoubleRight } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Manager_web_portal from '../../../layout/Manager_web_portal';
import * as XLSX from 'xlsx';
import { CompletedPlanHeaderSkeleton, CompletedPlanTableSkeleton } from '../../../components/Skeleton';
import {
  buildClosedEnrollmentCacheKey,
  getClosedEnrollmentCache,
  setClosedEnrollmentCache
} from '../../../utils/closedEnrollmentCache';
import { Base_url } from '../../../utils/Base_url';

const API_BASE = `${Base_url}/v1/worker`;
const perPage = 8;

const CompletedPlan = () => {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchClosedEnrollments = async ({
    pageNum = page,
    searchTerm = debouncedSearch,
    paymentStatus = statusFilter,
    signal
  } = {}) => {
    const params = new URLSearchParams({
      page: String(pageNum),
      limit: String(perPage),
      paymentStatus
    });
    if (searchTerm) params.set('search', searchTerm);

    const res = await fetch(`${API_BASE}/closed-enrollement?${params.toString()}`, {
      signal
    });
    if (!res.ok) throw new Error('Server error');
    return res.json();
  };

  const applyPageResult = (json) => {
    if (json.success && Array.isArray(json.data)) {
      setData(json.data);
      setTotal(json.total || 0);
      setTotalPages(json.totalPages || 1);
      return true;
    }
    setData([]);
    setTotal(0);
    setTotalPages(1);
    return false;
  };

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    const cacheKey = buildClosedEnrollmentCacheKey({
      page,
      search: debouncedSearch,
      paymentStatus: statusFilter,
      limit: perPage
    });

    const cached = getClosedEnrollmentCache(cacheKey);
    if (cached) {
      applyPageResult(cached);
      setError(null);
      setLoading(false);
      return () => {
        active = false;
        controller.abort();
      };
    }

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const json = await fetchClosedEnrollments({
          pageNum: page,
          searchTerm: debouncedSearch,
          paymentStatus: statusFilter,
          signal: controller.signal
        });

        if (!active) return;

        applyPageResult(json);
        if (json.success && Array.isArray(json.data)) {
          setClosedEnrollmentCache(cacheKey, json);
        }
      } catch (err) {
        if (err.name === 'AbortError' || !active) return;
        setError('Failed to load data. Check internet or try again.');
        setData([]);
        setTotal(0);
        setTotalPages(1);
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
      controller.abort();
    };
  }, [page, debouncedSearch, statusFilter]);

  const exportExcel = async () => {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({
        export: 'true',
        paymentStatus: statusFilter
      });
      if (debouncedSearch) params.set('search', debouncedSearch);

      const res = await fetch(`${API_BASE}/closed-enrollement?${params.toString()}`);
      const json = await res.json();

      if (!json.success || !Array.isArray(json.data) || json.data.length === 0) {
        alert('No data to export');
        return;
      }

      const exportData = json.data.map((item) => ({
        'Investor Name': item.investorName,
        CNIC: item.investorCnic,
        'Plan Name': item.planName || 'N/A',
        'Total Deposit': item.totalDepositedAmount ?? item.totalDeposit,
        Profit: item.totalProfit,
        Loss: item.totalLoss || 0,
        Deduction: item.totalDeduction,
        'Net Amount':
          (item.totalDepositedAmount ?? item.totalDeposit ?? 0) +
          (item.totalProfit || 0) -
          (item.totalLoss || 0) -
          (item.totalDeduction || 0),
        Years: item.numberOfYears,
        'Payment Frequency': item.paymentFrequency,
        Status: item.status,
        'Payment Status': item.paymentStatus || 'pending',
        'Deposit Date': item.firstDepositDate
          ? new Date(item.firstDepositDate).toLocaleDateString()
          : new Date(item.enrollmentDate).toLocaleDateString()
      }));

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Closed Enrollments');
      XLSX.writeFile(wb, `Closed_Enrollments_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error(err);
      alert('Failed to export data');
    } finally {
      setIsExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PK', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-PK');
  };

  return (
    <Manager_web_portal
      language={<p className="sm:block hidden">English | Urdu</p>}
      ToggleBtn={
        <div className="flex gap-4 items-center">
          <HiOutlineArrowLeft />
          <h2 className="text-xl font-semibold">Closed Enrollments</h2>
        </div>
      }
      title_props={
        <div className="w-96 relative">
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-full outline-none focus:ring-2 focus:ring-secondary focus:border-transparent transition-all"
          />
          <button className="absolute right-1 top-1 bg-secondary hover:bg-primary text-white px-4 py-2 rounded-full transition-colors">
            <IoSearchOutline size={20} />
          </button>
        </div>
      }
    >
      <div className="w-full mb-6 sm:hidden block relative">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, CNIC, plan..."
          className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-full outline-none focus:ring-2 focus:ring-secondary focus:border-transparent"
        />
        <button className="absolute right-1 top-1 bg-secondary hover:bg-primary text-white px-4 py-2 rounded-full transition-colors">
          <IoSearchOutline size={20} />
        </button>
      </div>

      {loading ? (
        <CompletedPlanHeaderSkeleton showFilters />
      ) : (
        <>
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 hidden sm:block">Closed Enrollments</h2>
              <p className="text-sm text-gray-600 mt-1">
                Showing {total} closed enrollment{total !== 1 ? 's' : ''}
                {debouncedSearch && ` for "${debouncedSearch}"`}
              </p>
            </div>
            <button
              onClick={exportExcel}
              disabled={isExporting || total === 0}
              className={`px-4 py-2 text-white rounded-lg flex items-center gap-2 transition-colors ${
                isExporting || total === 0
                  ? 'bg-green-400 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              {isExporting ? 'Exporting...' : 'Export Excel'}
            </button>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            {['all', 'pending', 'paid', 'adjusted', 'partial paid', 're invested'].map((status) => (
              <button
                key={status}
                type="button"
                aria-pressed={statusFilter === status}
                onClick={() => {
                  setStatusFilter(status);
                  setPage(1);
                }}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors capitalize ${
                  statusFilter === status
                    ? 'bg-secondary text-white border-secondary'
                    : 'bg-white text-gray-600 border-gray-300 hover:border-secondary hover:text-secondary'
                }`}
              >
                {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </>
      )}

      {loading && <CompletedPlanTableSkeleton rows={perPage} showActions={false} />}

      {error && !loading && (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 text-red-600 mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-xl text-red-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-6 py-2 bg-secondary hover:bg-primary text-white rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      )}

      {!loading && !error && data.length === 0 && (
        <div className="text-center py-20">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <p className="text-xl text-gray-500 mb-2">No closed enrollments found</p>
          <p className="text-gray-400">
            {debouncedSearch || statusFilter !== 'all'
              ? 'Try a different search term or payment status'
              : 'All plans are currently active'}
          </p>
        </div>
      )}

      {!loading && !error && data.length > 0 && (
        <>
          <div className="overflow-x-auto rounded-xl shadow-lg border border-gray-200">
            <div className="min-w-[1400px]">
              <table className="w-full bg-white">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[180px]">
                      Investor Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[150px]">
                      CNIC
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                      Plan Name
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[160px]">
                      Deposit
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                      Profit
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                      Loss
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px]">
                      Deduction
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[160px]">
                      Net Amount
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[100px]">
                      Years
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[140px] whitespace-nowrap">
                      Payment Status
                    </th>
                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700 uppercase tracking-wider min-w-[180px] whitespace-nowrap">
                      Deposit Date
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.map((item) => {
                    const loss = item.totalLoss || 0;
                    const net =
                      (item.totalDepositedAmount ?? item.totalDeposit ?? 0) +
                      (item.totalProfit || 0) -
                      loss -
                      (item.totalDeduction || 0);
                    return (
                      <tr key={item._id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                          <Link
                            onClick={() => localStorage.setItem('iName', item?.investorName)}
                            to={`/manager/closed-deposits/${item?.investorCnic}/${item?._id}`}
                            className="font-medium text-secondary hover:text-primary transition-colors"
                          >
                            {item.investorName}
                          </Link>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[150px]">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                            {item.investorCnic}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                            {item.planName || 'N/A'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[160px]">
                          <div className="text-sm text-gray-900 font-medium">
                            Rs. {formatCurrency(item.totalDepositedAmount)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <div className="text-sm text-green-600 font-medium">
                            +Rs. {formatCurrency(item.totalProfit)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <div
                            className={`text-sm font-medium ${
                              loss > 0 ? 'text-red-600' : 'text-gray-400'
                            }`}
                          >
                            {loss > 0 ? `-Rs. ${formatCurrency(loss)}` : 'Rs. 0'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <div className="text-sm text-red-600 font-medium">
                            -Rs. {formatCurrency(item.totalDeduction)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[160px]">
                          <div
                            className={`text-sm font-bold px-3 py-2 rounded-lg ${
                              net >= 0 ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'
                            }`}
                          >
                            Rs. {formatCurrency(net)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[100px]">
                          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
                            {item.numberOfYears} {item.numberOfYears === 1 ? 'year' : 'years'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[140px]">
                          <span
                            className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                              (item.paymentStatus || 'pending') === 'pending'
                                ? 'bg-amber-100 text-amber-800'
                                : (item.paymentStatus || '').toLowerCase() === 'paid'
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {item.paymentStatus || 'pending'}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap min-w-[180px]">
                          <div className="flex flex-col">
                            <span className="text-sm text-gray-900 whitespace-nowrap">
                              {formatDate(item.firstDepositDate || item.enrollmentDate)}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row justify-between items-center gap-4 py-6 px-2">
            <div className="text-sm text-gray-600">
              Showing {total === 0 ? 0 : (page - 1) * perPage + 1} to {Math.min(page * perPage, total)} of{' '}
              {total} entries
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setPage(1)}
                disabled={page === 1}
                title="First page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                }`}
              >
                <FaAngleDoubleLeft />
              </button>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                title="Previous page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  page === 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                }`}
              >
                <FaAngleLeft />
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg transition-colors ${
                        page === pageNum
                          ? 'bg-secondary text-white shadow-sm'
                          : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                title="Next page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  page === totalPages
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                }`}
              >
                <FaAngleRight />
              </button>
              <button
                type="button"
                onClick={() => setPage(totalPages)}
                disabled={page === totalPages || totalPages <= 1}
                title="Last page"
                className={`flex items-center justify-center w-10 h-10 rounded-lg transition-colors ${
                  page === totalPages || totalPages <= 1
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-secondary hover:bg-primary text-white shadow-sm hover:shadow'
                }`}
              >
                <FaAngleDoubleRight />
              </button>
            </div>
          </div>
        </>
      )}
    </Manager_web_portal>
  );
};

export default CompletedPlan;
